import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Ban, CheckCircle2, MessageCircle, XCircle } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Toolbar } from '@/components/Toolbar'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { EmptyState } from '@/components/EmptyState'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useActOnApproval, useApprovalInstance } from '@/features/approvals/queries'
import { formatDate, formatDateTime } from '@/lib/locale'
import { formatInvoiceMoney } from '@/features/invoicing/format'
import type { EnrichedPayload } from '@/features/approvals/types'

export function ApprovalInstanceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const instanceId = Number(id)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { hasPermission, user } = useAuth()
  // AuthUser exposes `userId` (string from the JWT sub claim); the approval
  // row stores `requested_by_user_id` as an integer. Coerce both sides via
  // Number() so the self-approval guard below actually compares them.
  const currentUserId = user?.userId != null ? Number(user.userId) : null

  const { data, isLoading } = useApprovalInstance(instanceId)
  const actMut = useActOnApproval()

  const [actionOpen, setActionOpen] = useState<null | 'approve' | 'reject' | 'comment' | 'cancel'>(null)
  const [comment, setComment] = useState('')

  if (isLoading) {
    return <Page title="Approval"><Card><div className="flex justify-center py-20"><LoadingSpinner /></div></Card></Page>
  }
  if (!data?.instance) {
    return <Page title="Approval not found"><Card><EmptyState title="Approval not found" description="The approval instance doesn't exist or was deleted." /></Card></Page>
  }
  // `inst` is now narrowed; the remainder of the component treats it as
  // non-nullable. Closures below capture this binding, so TS preserves
  // the narrowing across onConfirmAction.
  const inst = data.instance

  const canAct = hasPermission('approvals.act')
  const activeStep = inst.steps.find((s) => s.status === 'pending')
  const isRequester = currentUserId != null && Number(inst.requested_by_user_id) === currentUserId
  const selfApprovalAllowed = !!(
    inst.policy_snapshot && (inst.policy_snapshot as Record<string, unknown>).self_approval_allowed
  )
  // Requester can comment/cancel but not approve/reject their own unless policy allows.
  const canDecide = canAct
    && inst.status === 'pending'
    && !!activeStep
    && (selfApprovalAllowed || !isRequester)
  const canCancel = inst.status === 'pending' && isRequester

  function openAction(a: 'approve' | 'reject' | 'comment' | 'cancel') {
    setComment('')
    setActionOpen(a)
  }

  async function onConfirmAction() {
    if (!actionOpen) return
    try {
      await actMut.mutateAsync({ id: inst.id, action: actionOpen, comment: comment || undefined })
      toast({ title: `Recorded: ${actionOpen}` })
      setActionOpen(null)
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Action failed', variant: 'destructive' })
    }
  }

  const statusVariant: Record<string, 'default' | 'secondary' | 'danger' | 'success' | 'warning'> = {
    pending: 'default', approved: 'success', rejected: 'danger',
    canceled: 'secondary', auto_approved: 'success', escalated: 'warning', expired: 'danger',
  }

  return (
    <Page
      title={`Approval #${inst.id}`}
      description={inst.resource_summary || inst.resource_type}
      actions={
        <Toolbar align="end">
          <Button variant="ghost" onClick={() => navigate('/approvals')}>
            <ArrowLeft className="mr-1 size-4" /> Back to inbox
          </Button>
          {canDecide ? (
            <>
              <Button onClick={() => openAction('approve')}>
                <CheckCircle2 className="mr-1 size-4" /> Approve
              </Button>
              <Button variant="danger" onClick={() => openAction('reject')}>
                <XCircle className="mr-1 size-4" /> Reject
              </Button>
            </>
          ) : null}
          {canAct || canCancel ? (
            <Button variant="secondary" onClick={() => openAction('comment')}>
              <MessageCircle className="mr-1 size-4" /> Comment
            </Button>
          ) : null}
          {canCancel ? (
            <Button variant="ghost" onClick={() => openAction('cancel')}>
              <Ban className="mr-1 size-4 text-destructive" /> Cancel request
            </Button>
          ) : null}
        </Toolbar>
      }
    >
      {inst.status === 'pending' && isRequester && !selfApprovalAllowed ? (
        <Card className="mb-4 border-warning/40 bg-warning/5">
          <div className="p-4 text-sm">
            <div className="font-medium">You can't approve your own request</div>
            <div className="mt-1 text-muted-foreground">
              You requested this approval, and the policy{' '}
              {inst.policy_name ? <span className="font-medium">"{inst.policy_name}"</span> : null}{' '}
              does not allow self-approval. Either ask another approver on this step to act, or{' '}
              <Link to="/approvals/policies" className="text-primary underline">
                edit the policy
              </Link>{' '}
              to enable self-approval.
            </div>
          </div>
        </Card>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="space-y-4 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant={statusVariant[inst.status] || 'secondary'}>{inst.status.replace(/_/g, ' ')}</Badge>
              <code className="rounded bg-muted px-2 py-0.5 text-xs">{inst.resource_type} #{inst.resource_id}</code>
              {inst.policy_name ? <span className="text-sm text-muted-foreground">Policy: {inst.policy_name}</span> : null}
            </div>
            <div className="grid gap-3 text-sm md:grid-cols-2">
              <div>
                <div className="text-xs uppercase text-muted-foreground">Requested</div>
                <div>{formatDateTime(inst.requested_at)}</div>
                {inst.requested_by_email ? <div className="text-xs text-muted-foreground">{inst.requested_by_email}</div> : null}
              </div>
              <div>
                <div className="text-xs uppercase text-muted-foreground">Due</div>
                <div>{inst.due_at ? formatDateTime(inst.due_at) : <span className="text-muted-foreground">-</span>}</div>
              </div>
              {inst.finished_at ? (
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Finished</div>
                  <div>{formatDateTime(inst.finished_at)}</div>
                  {inst.final_decision ? <div className="text-xs text-muted-foreground">{inst.final_decision}</div> : null}
                </div>
              ) : null}
            </div>

            <hr />

            <div>
              <div className="mb-2 text-sm font-semibold">Ladder</div>
              <ol className="space-y-2 text-sm">
                {inst.steps.map((s) => {
                  const stepBadge =
                    s.status === 'approved' ? <Badge variant="success">approved</Badge> :
                    s.status === 'rejected' ? <Badge variant="danger">rejected</Badge> :
                    s.status === 'skipped'  ? <Badge variant="secondary">skipped</Badge> :
                    s.status === 'expired'  ? <Badge variant="danger">expired</Badge> :
                    <Badge variant="default">pending</Badge>
                  const rule = s.approver_rule?.role
                    ? `Role: ${s.approver_rule.role}`
                    : s.approver_rule?.user_id ? `User #${s.approver_rule.user_id}` : '-'
                  return (
                    <li key={s.id} className="rounded-md border p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="font-medium">Step {s.step_order}</div>
                        {stepBadge}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {rule} · {s.collected_approvals}/{s.required_approver_count} collected
                        {s.parallel ? ' · parallel' : ''}
                      </div>
                    </li>
                  )
                })}
              </ol>
            </div>

            <hr />

            <div>
              <div className="mb-2 text-sm font-semibold">Request details</div>
              <EnrichedPayloadView
                payload={inst.enriched_payload}
                resourceType={inst.resource_type}
                resourceId={inst.resource_id}
              />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="mb-3 text-sm font-semibold">Activity</div>
            {inst.actions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No actions recorded yet.</p>
            ) : (
              <ol className="space-y-3 text-sm">
                {inst.actions.map((a) => (
                  <li key={a.id} className="flex gap-3">
                    <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{a.action}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(a.created_at)}
                        {a.actor_email ? ` · ${a.actor_email}` : ''}
                      </div>
                      {a.comment ? <div className="mt-1 text-xs">{a.comment}</div> : null}
                    </div>
                  </li>
                ))}
              </ol>
            )}
            <hr className="my-4" />
            <div className="text-xs text-muted-foreground">
              <div className="mb-1"><Link to="/approvals" className="text-primary hover:underline">← Back to inbox</Link></div>
              <div><Link to="/approvals/policies" className="text-primary hover:underline">View policies</Link></div>
            </div>
          </div>
        </Card>
      </div>

      <Modal.Confirm
        open={actionOpen !== null}
        onOpenChange={(o) => { if (!o) setActionOpen(null) }}
        title={
          actionOpen === 'approve' ? `Approve request #${inst.id}`
          : actionOpen === 'reject'  ? `Reject request #${inst.id}`
          : actionOpen === 'cancel'  ? `Cancel request #${inst.id}`
          : `Add a comment to #${inst.id}`
        }
        body={
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {actionOpen === 'approve' ? 'Advances the ladder. The underlying resource is only applied once every step has been fully approved.'
               : actionOpen === 'reject' ? 'Closes the request as rejected. The underlying mutation does not run.'
               : actionOpen === 'cancel' ? 'Closes the request as canceled (typically only the requester does this).'
               : 'Adds an unstructured comment to the audit trail - no status change.'}
            </p>
            <FormField
              label="Comment"
              control={
                <textarea
                  className="min-h-[72px] w-full rounded-md border bg-card px-3 py-2 text-sm"
                  value={comment}
                  onChange={(e) => setComment(e.currentTarget.value)}
                  placeholder="Optional"
                />
              }
            />
          </div>
        }
        confirmLabel={
          actionOpen === 'approve' ? 'Approve' :
          actionOpen === 'reject' ? 'Reject' :
          actionOpen === 'cancel' ? 'Cancel request' : 'Add comment'
        }
        confirmVariant={
          actionOpen === 'approve' ? 'primary' :
          actionOpen === 'reject' || actionOpen === 'cancel' ? 'danger' : 'primary'
        }
        busy={actMut.isPending}
        onConfirm={onConfirmAction}
      />
    </Page>
  )
}

function Row({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  )
}

function EnrichedPayloadView({
  payload,
  resourceType,
  resourceId,
}: {
  payload?: EnrichedPayload
  resourceType: string
  resourceId: number
}) {
  if (!payload || payload.kind === 'raw') {
    return (
      <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
        {resourceType} #{resourceId}
      </div>
    )
  }

  if (payload.kind === 'bill') {
    return (
      <div className="rounded-md border bg-muted/30 p-4">
        <div className="mb-2 flex items-center justify-between">
          <Link
            to={`/invoicing/bills/${resourceId}`}
            className="font-semibold text-primary hover:underline"
          >
            Bill {payload.bill_number}
          </Link>
          <Badge variant="secondary">{payload.status}</Badge>
        </div>
        <dl className="divide-y">
          <Row label="Vendor" value={
            <Link
              to={`/invoicing/vendors/${payload.vendor_id}`}
              className="text-primary hover:underline"
            >
              {payload.vendor_name}
            </Link>
          } />
          {payload.vendor_invoice_number ? (
            <Row label="Vendor invoice #" value={payload.vendor_invoice_number} />
          ) : null}
          <Row label="Issue date" value={formatDate(payload.issue_date)} />
          <Row label="Due date" value={formatDate(payload.due_date)} />
          <Row label="Subtotal" value={formatInvoiceMoney(payload.subtotal, payload.currency)} />
          {Number(payload.discount) > 0 ? (
            <Row label="Discount" value={`−${formatInvoiceMoney(payload.discount, payload.currency)}`} />
          ) : null}
          {Number(payload.shipping_amount) > 0 ? (
            <Row label="Shipping" value={formatInvoiceMoney(payload.shipping_amount, payload.currency)} />
          ) : null}
          {Number(payload.tax_total) > 0 ? (
            <Row label="Tax" value={formatInvoiceMoney(payload.tax_total, payload.currency)} />
          ) : null}
          <Row
            label={<span className="font-semibold">Total</span>}
            value={<span className="font-semibold">{formatInvoiceMoney(payload.total, payload.currency)}</span>}
          />
        </dl>
      </div>
    )
  }

  // invoice
  return (
    <div className="rounded-md border bg-muted/30 p-4">
      <div className="mb-2 flex items-center justify-between">
        <Link
          to={`/invoicing/invoices/${resourceId}`}
          className="font-semibold text-primary hover:underline"
        >
          Invoice {payload.invoice_number}
        </Link>
        <Badge variant="secondary">{payload.status}</Badge>
      </div>
      <dl className="divide-y">
        <Row label="Customer" value={
          <Link
            to={`/invoicing/customers/${payload.customer_id}`}
            className="text-primary hover:underline"
          >
            {payload.customer_name}
          </Link>
        } />
        {payload.po_number ? <Row label="PO #" value={payload.po_number} /> : null}
        <Row label="Issue date" value={formatDate(payload.issue_date)} />
        <Row label="Due date" value={formatDate(payload.due_date)} />
        <Row label="Subtotal" value={formatInvoiceMoney(payload.subtotal, payload.currency)} />
        {Number(payload.discount) > 0 ? (
          <Row label="Discount" value={`−${formatInvoiceMoney(payload.discount, payload.currency)}`} />
        ) : null}
        {Number(payload.shipping_amount) > 0 ? (
          <Row label="Shipping" value={formatInvoiceMoney(payload.shipping_amount, payload.currency)} />
        ) : null}
        {Number(payload.tax_total) > 0 ? (
          <Row label="Tax" value={formatInvoiceMoney(payload.tax_total, payload.currency)} />
        ) : null}
        <Row
          label={<span className="font-semibold">Total</span>}
          value={<span className="font-semibold">{formatInvoiceMoney(payload.total, payload.currency)}</span>}
        />
      </dl>
    </div>
  )
}
